/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import {
  Box,
  Button,
  DialogContentText,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import { CustomFilterV2, DialogDefaultV2, TableV2 } from '../../../components/v2';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as actionsProgramacaoDiarias from '../../../store/programacaoDiaria/actions';
import * as Roles from '../../../constants/role';
import { isNotAdmin } from 'utils';
import {
  TIPO_PROGRAMACAO_DIARIA_DESLOCAMENTO,
  TIPO_PROGRAMACAO_DIARIA_INSPECAO,
  TIPO_PROGRAMACAO_DIARIA_MANUTENCAO_VEICULOS,
} from '../../../constants/ids';
import Formulario from './Formulario';

const cellContentSx = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  height: '100%',
  minWidth: 0,
  width: '100%',
};

const defaultFilter = authData => ({
  inicio: moment().subtract(1, 'month').startOf('month').valueOf(),
  fim: moment().endOf('month').valueOf(),
  usuarioId: isNotAdmin(authData.role) ? [authData.id] : [],
});

const renderDescricaoTipo = row => {
  if (TIPO_PROGRAMACAO_DIARIA_DESLOCAMENTO === row.tipoId) {
    return (
      <Box sx={cellContentSx}>
        <Typography variant="body2">
          <Box component="span" sx={{ fontWeight: 700 }}>De: </Box>
          {row.cidadeDe}
        </Typography>
        <Typography variant="body2">
          <Box component="span" sx={{ fontWeight: 700 }}>Para: </Box>
          {row.cidadePara}
        </Typography>
      </Box>
    );
  }

  if (TIPO_PROGRAMACAO_DIARIA_INSPECAO === row.tipoId) {
    return (
      <Box sx={cellContentSx}>
        <Typography variant="body2">
          <Box component="span" sx={{ fontWeight: 700 }}>Nsirs: </Box>
          {row.nsirCodes ? row.nsirCodes.join(', ') : '-'}
        </Typography>
      </Box>
    );
  }

  if (TIPO_PROGRAMACAO_DIARIA_MANUTENCAO_VEICULOS === row.tipoId) {
    return (
      <Box sx={cellContentSx}>
        <Typography variant="body2">
          <Box component="span" sx={{ fontWeight: 700 }}>Veículo: </Box>
          {row.veiculo}
        </Typography>
      </Box>
    );
  }

  return <Box sx={cellContentSx}>-</Box>;
};

const ProgramacaoDiariasV2 = ({ exportProgramacaoDiaria, authData }) => {
  const [open, setOpen] = useState(false);
  const [id, setId] = useState(null);
  const [localFilter, setLocalFilter] = useState(defaultFilter(authData));
  const [openExport, setOpenExport] = useState(false);
  const [reload, setReload] = useState(false);
  const [openFilter, setOpenFilter] = useState(false);

  const onEdit = rowId => {
    setOpen(true);
    setId(rowId);
  };

  const onAdd = () => {
    setOpen(true);
    setId(null);
  };

  const onClose = isSave => {
    if (isSave) {
      setReload(!reload);
    }
    setOpen(false);
    setId(null);
  };

  const columns = [
    {
      field: 'referenceDayFormat',
      headerName: 'Data',
      minWidth: 170,
      flex: 1,
      renderCell: params => (
        <Box sx={cellContentSx}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {params.row.referenceDayFormat}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Criado em {params.row.createdAt}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'inspetorName',
      headerName: 'Inspetor',
      minWidth: 230,
      flex: 1.3,
      renderCell: params => (
        <Box sx={cellContentSx}>
          <Typography variant="body2">
            <Box component="span" sx={{ fontWeight: 700 }}>Inspetor: </Box>
            {params.row.inspetorName}
          </Typography>
          {params.row.inspetorAuxiliarName && (
            <Typography variant="caption" color="text.secondary">
              <Box component="span" sx={{ fontWeight: 700 }}>Ajudante/Observador: </Box>
              {params.row.inspetorAuxiliarName}
            </Typography>
          )}
        </Box>
      ),
    },
    { field: 'tipo', headerName: 'Tipo da Programação', minWidth: 180, flex: 1 },
    {
      field: 'descricaoTipo',
      headerName: 'Descrição do Tipo',
      minWidth: 220,
      flex: 1.2,
      renderCell: params => renderDescricaoTipo(params.row),
    },
    {
      field: 'descricao',
      headerName: 'Descrição',
      minWidth: 220,
      flex: 1.3,
      renderCell: params => <Box sx={cellContentSx}>{params.row.descricao || '-'}</Box>,
    },
  ];

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ pr: 2 }}
        >
          <Typography variant="h4">Programação Diária</Typography>
          <Button
            variant="outlined"
            startIcon={<FilterListOutlinedIcon />}
            onClick={() => setOpenFilter(true)}
          >
            Filtros
          </Button>
        </Stack>

        <TableV2
          useApi
          fetchUrl="programacao-diaria/filter"
          paginationUrl="/v2/programacao-diaria"
          filterData={localFilter}
          refreshKey={reload}
          rowHeight={82}
          headerActions={[
            { label: 'Novo', onClick: onAdd, startIcon: <AddIcon />, variant: 'contained' },
            {
              label: 'Exportar',
              onClick: () => setOpenExport(true),
              startIcon: <DescriptionOutlinedIcon />,
              variant: 'outlined',
            },
          ]}
          columns={columns}
          onEdit={onEdit}
          emptyMessage="Nenhuma programação diária encontrada"
        />

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          title="Filtros"
          fullWidth
          defaultFilter={defaultFilter(authData)}
          appliedFilter={localFilter}
          fields={[
            { field: 'useDates', useDateTypes: false, useDateTypesReadOnly: true },
            {
              field: 'useUsuarios',
              md: 6,
              xs: 12,
              multiSelect: true,
              disabled: (Roles.ADMIN !== authData.role && Roles.SUPER_ADMIN !== authData.role),
            },
            {
              field: 'useOption',
              md: 6,
              xs: 12,
              code: 'TIPO_PROGRAMACAO_DIARIA',
              label: 'Tipo da Programação',
              key: 'tipoIds',
            },
            { field: 'useNsirs', md: 6, xs: 12 },
            { field: 'useMalha', md: 6, xs: 12, multiSelect: true },
            { field: 'useGerencia', md: 6, xs: 12, multiSelect: true },
            { field: 'usePolo', md: 6, xs: 12, multiSelect: true },
            { field: 'useTipoServico', md: 6, xs: 12 },
          ]}
          onFilter={filterData => {
            setLocalFilter(filterData);
            setOpenFilter(false);
          }}
        />

        <Formulario id={id} open={open} onClose={onClose} />

        <DialogDefaultV2
          open={openExport}
          handlerCancel={() => setOpenExport(false)}
          handlerConfirm={() => {
            exportProgramacaoDiaria(localFilter, () => {
              setOpenExport(false);
            });
          }}
          title="Confirmação"
          confirmMessage="Sim"
          cancelMessage="Não"
        >
          <DialogContentText>Deseja baixar todas as informações filtradas?</DialogContentText>
        </DialogDefaultV2>
      </Stack>
    </Box>
  );
};

ProgramacaoDiariasV2.propTypes = {
  authData: PropTypes.object.isRequired,
  exportProgramacaoDiaria: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  authData: selectorsAuth.getAuth(state),
});

const mapDispatchToProps = dispatch => bindActionCreators({
  ...actionsProgramacaoDiarias,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ProgramacaoDiariasV2));
